import { useEffect, type RefObject } from 'react';

import { focusFirstFocusable, getFocusableElements, handleFocusTrapKeyDown } from './focusTrap.js';

export interface UseFocusTrapOptions {
  /** When true, traps focus and locks body scroll. */
  active: boolean;
  /** Called when Escape is pressed while the trap is active. */
  onEscape?: () => void;
  /** When true, restores focus to the previously focused element on deactivate. Default true. */
  restoreFocus?: boolean;
  /** When true, locks document body scroll while active. Default true. */
  lockScroll?: boolean;
}

/**
 * Traps keyboard focus within a container and optionally locks body scroll.
 * Used by modal dialogs and similar overlay surfaces.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  { active, onEscape, restoreFocus = true, lockScroll = true }: UseFocusTrapOptions,
): void {
  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    const previousActive = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    if (lockScroll) {
      document.body.style.overflow = 'hidden';
    }

    focusFirstFocusable(container);

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape?.();
        return;
      }

      const trapContainer = containerRef.current;
      if (!trapContainer) return;

      handleFocusTrapKeyDown(event, trapContainer);
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      if (lockScroll) {
        document.body.style.overflow = previousOverflow;
      }

      if (restoreFocus && previousActive instanceof HTMLElement) {
        previousActive.focus();
      }
    };
  }, [active, containerRef, lockScroll, onEscape, restoreFocus]);
}

export { getFocusableElements, focusFirstFocusable };
